import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import MuiSwitch from '@material-ui/core/Switch';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import TabPanel from '@material-ui/lab/TabPanel';
import { AppBar, Tab, makeStyles } from '@material-ui/core';

import _ from 'lodash';

import { parse, stringify } from 'yaml';

import { HandleAction, makePhase } from '../Utils/utils.js';

import { ShowSetting } from '../FormWidgets/ShowSliderSetting';
import {
  SettingGroup,
  SettingGroupBlock,
  SettingGroupGrid,
  ExpandableSettingsGroupGrid,
  ValueBox
} from '../FormWidgets/SettingGroup';
import { LoadSettingsDialog } from '../Utils/load_settings';
import {
  AbsoluteRelativeSelect,
  DayWeekSelect,
  DayWeekToggle,
  EnableDisableDaytimeSelect,
  EnableDayTimeDisableSelect,
  EventGapSelect
} from '../FormWidgets/Select.js';
import { MyTimePicker } from '../FormWidgets/DatePicker.js';
import { EditableLabel } from '../FormWidgets/EditableLabel.js';
import Desc from '../FormWidgets/Description';

import getStateManager from '../StateManager.js';

import { settingControls, useSavedSettings, writeTempSettings } from './settingFunctions';

import { SettingActions } from '../components';
import { ExpandMore } from '../Utils/Expand';

import { EventTimerIndustrialPro, IntervalTimerIndustrialPro } from './TimingSettings.js';
import { Pump } from './NutrientSettings.js';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.primary
  }
}));

function GetDays(index, values, num_phases) {
  const arr = Array.from(
    { length: num_phases },
    (_, i) => values.schedule_meta['len_phase_' + (i + 1) + '_days']
  );
  if (index === 0) return 0;
  else return arr.slice(0, index).reduce((acc, cur) => acc + cur, 0);
}

function GetWeeks(index, values, num_phases) {
  const arr = Array.from({ length: num_phases }, (_, i) =>
    Math.ceil(values.schedule_meta['len_phase_' + (i + 1) + '_days'] / 7)
  );
  if (index === 0) return 0;
  else return arr.slice(0, index).reduce((acc, cur) => acc + cur, 0);
}

export default function Scheduling({ controller, parent, ...other }) {
  let { t } = useTranslation();
  const stateManager = getStateManager();

  const tab_id = 'scheduling';
  const [values, setValues] = useState(_.cloneDeep(parent.settings[tab_id]));
  const controls = settingControls(controller, parent, tab_id, values, setValues);
  const { openLoadSettingsDialog, closeLoadSettingsDialog } = useSavedSettings(
    parent,
    controls,
    tab_id
  );

  let num_phases = values.schedule_values.scheduling_values.length ?? 0;

  const [useWeekDay, setUseWeekDay] = useState(
    Array.from(
      { length: num_phases },
      (_, i) => values.schedule_meta['len_phase_' + (i + 1) + '_days'] % 7 === 0
    )
  );

  const callAction = (type, value) => {
    if (parent.connection.status !== 'bad') {
      HandleAction(stateManager.wsManager, parent, type, value);
    }
  };

  const nutrientFields = ['ec_setpoint', 'ph_setpoint', 'temp_setpoint', 'pump_cnt_used', 'pumps'];
  const nutrientPumpFields = ['name', 'value', 'type', 'unit'];
  const irrigationFields = [
    'enable_irrigation',
    'irrigation_p0_dry_back_abs',
    'irrigation_p2_offset_target_vwc',
    'irrigation_p2_target_vwc_abs',
    'irrigation_p2_dry_back_abs',
    'irrigation_p2_end_before_sunset'
  ];
  const allFields = [...nutrientFields, ...nutrientPumpFields, ...irrigationFields];

  const transform_sent_settings = (settings) => {
    // Convert irrigation_p2_end_before_sunset from minutes to a string format "HH:MM:SS"
    settings.scheduling_values.forEach((setting) => {
      setting.irrigation_settings.forEach((irrigation) => {
        if (typeof irrigation.irrigation_p2_end_before_sunset === 'number') {
          const totalMinutes = irrigation.irrigation_p2_end_before_sunset;
          let hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          if (hours >= 24) {
            hours -= 24;
          }
          irrigation.irrigation_p2_end_before_sunset = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
        }
      });
    });
    let updated_settings = {
      schedule_values_count: values.schedule_values.scheduling_values.length,
      scheduling_values: settings.scheduling_values
    };
    return updated_settings;
  };

  const ToggleDayWeek = (index, value) => {
    // Update use week/day array:.
    let updatedPhasesDays = useWeekDay;
    useWeekDay[index] = value;
    setUseWeekDay(updatedPhasesDays);
    // modify number of days matching days/ weeks
    const id = 'len_phase_' + (index + 1) + '_days';
    // Update value in schedule-meta-struct
    controls.setValue(`schedule_meta[${id}]`, values.schedule_meta[id] * (value ? 7 : 1 / 7));
  };

  /** Overwrite saveSettings() to send two individual messages for scheduling_values and scheduling_meta.
   *  This is just to be compatible for the ESP-based controller backends. We should change this in the backend
   *  and then remove this.
   */
  controls.saveSettings = (transform = null, do_transfrom = false) => {
    let _schedule_values = values.schedule_values;
    _schedule_values.scheduling_values = _schedule_values.scheduling_values.filter(
      (p) => p._deleted !== true
    );

    stateManager.saveSettings(controller, parent, _schedule_values, 'schedule_values', transform);
    stateManager.saveSettings(controller, parent, values.schedule_meta, 'schedule_meta');

    // merge schedule settings in case the controller is not re-sending its new settings (true for simulation. Don't know for Industrial Lite)
    parent.settings['scheduling'] = {
      schedule_meta: parent.settings.schedule_meta,
      schedule_values: parent.settings.schedule_values
    };

    delete parent.temp_settings[tab_id];
    writeTempSettings(parent);
  };

  const save = () => {
    controls.saveSettings(transform_sent_settings, true);
    setUseWeekDay(
      Array.from(
        { length: num_phases + 100 }, // HotFix: num_phases is not updated in this context
        (_, i) => values.schedule_meta['len_phase_' + (i + 1) + '_days'] % 7 === 0
      )
    );

    setDayRange(
      Array.from(
        { length: num_phases + 100 }, // HotFix: num_phases is not updated in this context
        (_, i) =>
          `${GetDays(i, values, num_phases) + 1} - ${
            GetDays(i, values, num_phases) + values.schedule_meta['len_phase_' + (i + 1) + '_days']
          }`
      )
    );

    setWeekRange(
      Array.from(
        { length: num_phases + 100 }, // HotFix: num_phases is not updated in this context
        (_, i) =>
          // Check
          `${GetWeeks(i, values, num_phases) + 1} - ${
            GetWeeks(i, values, num_phases) +
            Math.ceil(values.schedule_meta['len_phase_' + (i + 1) + '_days'] / 7)
          }`
      )
    );
  };

  const addPhase = () => {
    const phase_id = values.schedule_values.scheduling_values.length + 1;

    let phase = makePhase({ name_phase_short: 'Your Name', phase_id: phase_id });
    let _phases = [...values.schedule_values.scheduling_values];
    _phases.push(phase);
    //setPhases(phases);

    controls.setValue(
      [
        'schedule_meta.active_phase_cnt',
        `schedule_meta.len_phase_${phase_id}_days`,
        'schedule_values.scheduling_values'
      ],
      [values.schedule_meta.active_phase_cnt + 1, 14, _phases]
    );
    useWeekDay.push(true);
  };

  const delPhase = (index) => {
    // Empty phases and reduce number of active phases

    let _phases = values.schedule_values.scheduling_values;
    _phases[index]._deleted = true;
    controls.setValue(
      ['schedule_meta.active_phase_cnt', 'schedule_values.scheduling_values'],
      [values.schedule_meta.active_phase_cnt - 1, _phases]
    );
  };

  const [dayRange, setDayRange] = useState(Array.from({ length: num_phases }, (_, i) => '???'));
  useEffect(() => {
    const dayRange = Array.from(
      { length: num_phases },
      (_, i) =>
        `${GetDays(i, values, num_phases) + 1} - ${
          values.schedule_values.scheduling_values.length > 1
            ? GetDays(i, values, num_phases) +
              values.schedule_meta['len_phase_' + (i + 1) + '_days']
            : t('forever')
        }`
    );
    setDayRange(dayRange);
  }, [values]);

  const [weekRange, setWeekRange] = useState(Array.from({ length: num_phases }, (_, i) => '???'));
  useEffect(() => {
    const weekRange = Array.from(
      { length: num_phases },
      (_, i) =>
        // Check
        `${GetWeeks(i, values, num_phases) + 1} - ${
          values.schedule_values.scheduling_values.length > 1
            ? GetWeeks(i, values, num_phases) +
              Math.ceil(values.schedule_meta['len_phase_' + (i + 1) + '_days'] / 7)
            : t('forever')
        }`
    );
    setWeekRange(weekRange);
  }, [values]);

  const [expand, setExpand] = useState(
    Array.from({ length: values.schedule_values.scheduling_values.length }, (_, i) =>
      controls.hasChanged(`schedule_values.scheduling_values[${i}]`)
    )
  );
  const handleExpandClick = (i) => {
    let modifiedExpand = expand;
    modifiedExpand[i] = !expand[i];
    setExpand(modifiedExpand);
  };

  // auto-expand new phase
  // or changed phaseson tab resume
  useEffect(() => {
    setExpand(
      Array.from(
        { length: values.schedule_values.scheduling_values.length },
        (p, i) => expand[i] || controls.hasChanged(`schedule_values.scheduling_values[${i}]`)
      )
    );
  }, [values]);

  const download = () => {
    const jsonObj = _.cloneDeep(values);
    const yaml = stringify(jsonObj);
    // Create a Blob from the YAML string
    const blob = new Blob([yaml], { type: 'text/yaml' });
    // Create a link element and set the download attribute with a filename
    const link = document.createElement('a');
    link.download = values.schedule_meta.name_short + '.yaml';
    // Create a URL for the Blob and set it as the href attribute
    link.href = URL.createObjectURL(blob);
    // Programmatically click the link to trigger the download
    link.click();
    // Clean up the URL object
    URL.revokeObjectURL(link.href);
  };

  const [yamlContent, setYamlContent] = useState(null);
  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setYamlContent(e.target.result);
      };
      reader.readAsText(file);
    }
  };
  const upload = () => {
    if (yamlContent) {
      try {
        // Parse the YAML content to a JSON object
        const jsonObject = parse(yamlContent);
        setValues({
          schedule_meta: jsonObject['schedule_meta'],
          schedule_values: jsonObject['schedule_values']
        });

        const updatedUseWeekDay = Array.from(
          { length: jsonObject.schedule_values.scheduling_values.length },
          (_, i) => jsonObject.schedule_meta['len_phase_' + (i + 1) + '_days'] % 7 === 0
        );
        setUseWeekDay(updatedUseWeekDay);

        // Fill up controller-values with detault values.
        if (
          parent.settings.scheduling.schedule_values.scheduling_values.length <
          values.schedule_values.scheduling_values.length
        ) {
          for (
            let i = parent.settings.schedule_values.scheduling_values.length + 1;
            i <= values.schedule_values.scheduling_values.length;
            i++
          ) {
            parent.settings.schedule_values.scheduling_values.push(
              makePhase({ name_phase_short: 'Your Name', phase_id: i })
            );
          }
        }
      } catch (error) {
        console.error('Error parsing YAML:', error);
      }
    } else {
      alert('No File selected.');
    }
  };

  const classes = useStyles();

  return (
    <React.Fragment>
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
        info="scheduling"
      />

      <SettingActions onReset={controls.resetSettings} onSave={save} />

      <SettingGroupGrid controller={controller} id={'save_and_load_hl'}>
        <ValueBox controller={controller} id={'scheduling_name_short'}>
          <EditableLabel
            label={t('current_schedule_short_name')}
            id={'name_short'}
            value={values.schedule_meta.name_short}
            handleInput={(id, event, value) => controls.setValue('schedule_meta.name_short', value)}
          />
        </ValueBox>
        <>
          <Button color="primary" onClick={download}>
            {t('download_current_schedule_to_file')}
          </Button>
          <br />
          <Button color="primary" onClick={upload}>
            {t('upload_schedule_from_file')}
          </Button>
          <input type="file" accept=".yaml,.yml" onChange={handleFileChange} />
        </>
      </SettingGroupGrid>

      <SettingGroupGrid controller={controller} id={'phases_hl'}>
        {values.schedule_values.scheduling_values.map((phase, i) =>
          phase._deleted !== true ? (
            <ValueBox>
              <Grid container>
                <Grid item xs={4}>
                  <EditableLabel
                    label={t('phase') + ' ' + phase.phase_id}
                    id={'phase_' + phase.phase_id}
                    value={phase.name_phase_short}
                    handleInput={(phase_id, event, value) =>
                      controls.setValue(
                        `schedule_values.scheduling_values[${i}].name_phase_short`,
                        value
                      )
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <DayWeekSelect
                    use_week={useWeekDay[i]}
                    value={values.schedule_meta[`len_phase_${i + 1}_days`]}
                    onChange={(event, value) =>
                      controls.setValue(`schedule_meta.len_phase_${i + 1}_days`, event.target.value)
                    }
                    style={{ minWidth: 90 }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <DayWeekToggle
                    value={useWeekDay[i]}
                    onChange={(event, value) => ToggleDayWeek(i, event.target.value)}
                    style={{ minWidth: 90 }}
                  />
                </Grid>
                <Grid item xs={1}>
                  <Button color="primary" onClick={() => delPhase(i)}>
                    <DeleteForeverIcon />
                  </Button>
                </Grid>
              </Grid>
            </ValueBox>
          ) : (
            <ValueBox>
              <Typography gutterBottom>{t('phase') + ' ' + phase.phase_id}</Typography>
              <br />
              {t('deleted')}
              <br />
              {t('click save to confirm')}
            </ValueBox>
          )
        )}
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography gutterBottom>
            <MuiSwitch
              checked={values.schedule_meta.enable_smooth_change === 1}
              onClick={(event) => {
                controls.setValue(
                  'schedule_meta.enable_smooth_change',
                  event.target.checked ? 1 : 0
                );
              }}
              color="primary"
            />
            <Desc id="enable_smooth_change" />
          </Typography>
        </ValueBox>
        <>
          <Button color="primary" onClick={addPhase}>
            {t('add_phase')}
          </Button>
          <br />
          <Button color="primary" onClick={() => callAction('action_current_day_in_grow_cycle')}>
            {t('reset_current_day_in_grow_cycle')}
          </Button>
        </>
      </SettingGroupGrid>

      <SettingGroupGrid controller={controller} id={'selection_hl'}>
        <>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <MuiSwitch
                checked={values.schedule_meta.enable_scheduling === 1}
                onClick={(event) => {
                  controls.setValue(
                    'schedule_meta.enable_scheduling',
                    event.target.checked ? 1 : 0
                  );
                }}
                color="primary"
              />
              <Desc id="enable_scheduling" />
            </Typography>
          </ValueBox>
          <ValueBox controller={controller} id={'scheduling_'}>
            {[
              'enable_humidity',
              'enable_temper_main',
              'enable_vpd',
              'enable_heating_mat',
              'enable_co2_concentr',
              'enable_co2_temper',
              'enable_day_night',
              'enable_dim_ch_1',
              'enable_nutrient_1',
              'enable_irrigation_1'
            ].map((id, i) => (
              <>
                <Typography gutterBottom>
                  <MuiSwitch
                    checked={values.schedule_meta[id] === 1}
                    onClick={(event) => {
                      controls.setValue(`schedule_meta[${id}]`, event.target.checked ? 1 : 0);
                    }}
                    color="primary"
                  />
                  <Desc id={id} />
                </Typography>
              </>
            ))}
          </ValueBox>
        </>
        <>
          <ValueBox controller={controller} id={'scheduling_'}>
            {controller.type !== 6000 &&
              [
                'enable_interval_1',
                'enable_interval_2',
                'enable_event_1',
                'enable_event_2',
                'enable_event_3',
                'enable_event_4',
                'enable_event_5',
                'enable_event_6',
                'enable_event_7',
                'enable_event_8'
              ].map((id, i) => (
                <>
                  <Typography gutterBottom>
                    <MuiSwitch
                      checked={values.schedule_meta[id] === 1}
                      onClick={(event) => {
                        controls.setValue(`schedule_meta[${id}]`, event.target.checked ? 1 : 0);
                      }}
                      color="primary"
                    />
                    <Desc id={id} />
                  </Typography>
                </>
              ))}

            {controller.type === 6000 &&
              [...parent.settings.timing.event_timer, ...parent.settings.timing.interval_timer].map(
                (t) => (
                  <>
                    <Typography gutterBottom>
                      <MuiSwitch
                        checked={
                          values.schedule_meta[
                            `enable_${t.cycles ? 'event' : 'interval'}_${t.id}`
                          ] === 1
                        }
                        onClick={(event) => {
                          controls.setValue(
                            `schedule_meta['enable_${t.cycles ? 'event' : 'interval'}_${t.id}']`,
                            event.target.checked ? 1 : 0
                          );
                        }}
                        color="primary"
                      />
                      <Desc id={`enable_${t.cycles ? 'event' : 'interval'}_${t.id + 1}`} />
                    </Typography>
                  </>
                )
              )}
          </ValueBox>
        </>
      </SettingGroupGrid>

      {values.schedule_values.scheduling_values.map((phase, i) =>
        phase._deleted !== true ? (
          <SettingGroupGrid label={t('phase') + ' ' + phase.phase_id} fullWidth={true}>
            <Grid container>
              <Grid item xs={2}>
                {phase.name_phase_short}
              </Grid>
              <Grid item xs={1}>
                {t('day')}: {dayRange[i]}
              </Grid>
              <Grid item xs={0}>
                {t('week')}: {weekRange[i]}
              </Grid>
            </Grid>

            <div style={{ textAlign: 'center', marginTop: '-5em' }}>
              <ExpandMore
                expand={expand[i]}
                onClick={() => handleExpandClick(i)}
                aria-expanded={expand[i]}
                aria-label="show more"
              >
                <ExpandMoreIcon className={classes.icon} />
              </ExpandMore>
            </div>

            <Collapse in={expand[i]} timeout="auto" unmountOnExit>
              <HumiditySettings
                index={i}
                values={values}
                phase={phase}
                controls={controls}
                controller={controller}
                parent={parent}
              />

              <TempSettings
                index={i}
                values={values}
                phase={phase}
                controls={controls}
                controller={controller}
                parent={parent}
              />

              <VpdSettings
                index={i}
                values={values}
                phase={phase}
                controls={controls}
                controller={controller}
                parent={parent}
              />

              <HeatingMatSettings
                index={i}
                values={values}
                phase={phase}
                controls={controls}
                controller={controller}
                parent={parent}
              />

              <Co2ConcentrationSettings
                index={i}
                values={values}
                phase={phase}
                controls={controls}
                controller={controller}
                parent={parent}
              />

              <Co2TemperSettings
                index={i}
                values={values}
                phase={phase}
                controls={controls}
                controller={controller}
                parent={parent}
              />

              <StartEndSettings
                index={i}
                values={values}
                phase={phase}
                controls={controls}
                controller={controller}
              />

              <DimChannelSettings
                index={i}
                values={values}
                phase={phase}
                controls={controls}
                controller={controller}
                parent={parent}
              />

              {controller.zones && (
                <MultipleNutrientSettingsTabs
                  index={i}
                  values={values}
                  phase={phase}
                  controls={controls}
                  controller={controller}
                  parent={parent}
                />
              )}

              {!controller.zones && (
                <NutrientSettings
                  index={i}
                  values={values}
                  phase={phase}
                  controls={controls}
                  controller={controller}
                  parent={parent}
                />
              )}

              {controller.zones && (
                <MultipleIrrigationSettingsTabs
                  index={i}
                  values={values}
                  phase={phase}
                  controls={controls}
                  controller={controller}
                  parent={parent}
                />
              )}

              {!controller.zones && (
                <IrrigationSettings
                  index={i}
                  values={values}
                  phase={phase}
                  controls={controls}
                  controller={controller}
                  parent={parent}
                />
              )}

              {controller.type !== 6000
                ? Array.from({ length: 2 }).map((elem, interval_num) => (
                    <IntervalSettings
                      id={interval_num + 1}
                      index={i}
                      values={values}
                      phase={phase}
                      controls={controls}
                      controller={controller}
                    />
                  ))
                : parent.settings.timing.interval_timer.map((timer, t) => (
                    <IntervalSettings
                      timer={timer}
                      id={timer.id}
                      event_num={timer.id}
                      index={t}
                      values={values}
                      setValues={setValues}
                      phase={phase}
                      controls={controls}
                      controllerType={controller.type}
                      controller={controller}
                    />
                  ))}

              {controller.type !== 6000
                ? Array.from({ length: 8 }).map((elem, event_num) => (
                    <EventSettings
                      event_num={event_num + 1}
                      index={i}
                      values={values}
                      setValues={setValues}
                      phase={phase}
                      controls={controls}
                      controllerType={event_num > 4 ? controller.type : undefined}
                    />
                  ))
                : parent.settings.timing.event_timer.map((timer, t) => (
                    <EventSettings
                      timer={timer}
                      event_num={timer.id}
                      index={t}
                      values={values}
                      setValues={setValues}
                      phase={phase}
                      controls={controls}
                      controllerType={controller.type}
                      controller={controller}
                    />
                  ))}
            </Collapse>
          </SettingGroupGrid>
        ) : (
          <SettingGroupGrid label={t('phase') + ' ' + phase.phase_id} fullWidth={true}>
            {t('deleted')}
            {t('click save to confirm')}
          </SettingGroupGrid>
        )
      )}

      <SettingActions onReset={controls.resetSettings} onSave={save} />
    </React.Fragment>
  );
}

function HumiditySettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.enable_humidity) {
    return (
      <SettingGroupGrid controller={controller} id={'humidity_sp'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].humidity_sp_day`}
          show={'humidity_sp_day'}
          controller={controller}
          cur_value={phase.humidity_sp_day}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index].humidity_sp_day
              : makePhase({ phase_id: index })
          }
          controls={controls}
        />
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].humidity_sp_night`}
          show={'humidity_sp_night'}
          controller={controller}
          cur_value={phase.humidity_sp_night}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                  .humidity_sp_night
              : makePhase({ phase_id: index })
          }
          controls={controls}
        />
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function TempSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.enable_temper_main) {
    return (
      <SettingGroupGrid controller={controller} id={'temp_sp'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].temp_sp_day`}
          show={'temp_sp_day'}
          controller={controller}
          cur_value={phase.temp_sp_day}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index].temp_sp_day
              : makePhase({ phase_id: index })
          }
          controls={controls}
          type="float"
        />
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].temp_sp_night`}
          show={'temp_sp_night'}
          controller={controller}
          cur_value={phase.temp_sp_night}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index].temp_sp_night
              : makePhase({ phase_id: index })
          }
          controls={controls}
          type="float"
        />
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function VpdSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.enable_vpd) {
    return (
      <SettingGroupGrid controller={controller} id={'vpd_sp'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].vpd_sp`}
          show={'vpd_sp'}
          controller={controller}
          cur_value={phase.vpd_sp}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index].vpd_sp
              : makePhase({ phase_id: index })
          }
          unit={controller.type !== 6000 && parent.settings.system.vpd_format === 0 ? 'hPa' : 'kPa'}
          controls={controls}
          num_digits={controller.type !== 6000 && parent.settings.system.vpd_format === 0 ? 1 : 2}
          type="float"
        />
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].vpd_sp_night`}
          show={'vpd_sp_night'}
          controller={controller}
          cur_value={phase.vpd_sp_night}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index].vpd_sp_night
              : makePhase({ phase_id: index })
          }
          unit={controller.type !== 6000 && parent.settings.system.vpd_format === 0 ? 'hPa' : 'kPa'}
          controls={controls}
          num_digits={controller.type !== 6000 && parent.settings.system.vpd_format === 0 ? 1 : 2}
          type="float"
        />
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography gutterBottom>
            <Desc id="vpd_calc_enable" show="vpd_calc_enable" />
          </Typography>
          <EnableDisableDaytimeSelect
            value={phase.vpd_calc_enable}
            name="vpd_calc_enable"
            onChange={(event, value) =>
              controls.setValue(
                `schedule_values.scheduling_values[${index}].vpd_calc_enable`,
                event.target.value
              )
            }
          />
        </ValueBox>
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function HeatingMatSettings({ index, values, phase, controls, controller, parent }) {
  let { t } = useTranslation();

  if (values.schedule_meta.enable_heating_mat) {
    return (
      <SettingGroupBlock label={t('temperature_setpoint_heating_mat')}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <Desc id="toggle_temp_heating_mat_day" />
            </Typography>
            <AbsoluteRelativeSelect
              value={phase.toggle_temp_heating_mat_day}
              name="toggle_temp_heating_mat_day"
              onChange={(event, value) =>
                controls.setValue(
                  `schedule_values.scheduling_values[${index}].toggle_temp_heating_mat_day`,
                  event.target.value
                )
              }
            />
          </ValueBox>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <Desc id="toggle_temp_heating_mat_night" />
            </Typography>
            <AbsoluteRelativeSelect
              value={phase.toggle_temp_heating_mat_night}
              name="toggle_temp_heating_mat_night"
              onChange={(event, value) =>
                controls.setValue(
                  `schedule_values.scheduling_values[${index}].toggle_temp_heating_mat_night`,
                  event.target.value
                )
              }
            />
          </ValueBox>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'stretch' }}>
          {phase.toggle_temp_heating_mat_day === 1 ? (
            <ShowSetting
              id={`schedule_values.scheduling_values[${index}].temp_sp_heating_mat_day_abs`}
              show={'temp_sp_heating_mat_day_abs'}
              controller={controller}
              cur_value={phase.temp_sp_heating_mat_day_abs}
              controller_value={
                parent.settings.scheduling.schedule_values.scheduling_values.length > index
                  ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                      .temp_sp_heating_mat_day_abs
                  : makePhase({ phase_id: index })
              }
              controls={controls}
              type="float"
            />
          ) : (
            <ShowSetting
              id={`schedule_values.scheduling_values[${index}].temp_sp_heating_mat_day_rel`}
              show={'temp_sp_heating_mat_day_rel'}
              controller={controller}
              cur_value={phase.temp_sp_heating_mat_day_rel}
              controller_value={
                parent.settings.scheduling.schedule_values.scheduling_values.length > index
                  ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                      .temp_sp_heating_mat_day_rel
                  : makePhase({ phase_id: index })
              }
              controls={controls}
              type="float"
            />
          )}
          {phase.toggle_temp_heating_mat_night === 1 ? (
            <ShowSetting
              id={`schedule_values.scheduling_values[${index}].temp_sp_heating_mat_night_abs`}
              show={'temp_sp_heating_mat_night_abs'}
              controller={controller}
              cur_value={phase.temp_sp_heating_mat_night_abs}
              controller_value={
                parent.settings.scheduling.schedule_values.scheduling_values.length > index
                  ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                      .temp_sp_heating_mat_night_abs
                  : makePhase({ phase_id: index })
              }
              controls={controls}
              type="float"
            />
          ) : (
            <ShowSetting
              id={`schedule_values.scheduling_values[${index}].temp_sp_heating_mat_night_rel`}
              show={'temp_sp_heating_mat_night_rel'}
              controller={controller}
              cur_value={phase.temp_sp_heating_mat_night_rel}
              controller_value={
                parent.settings.scheduling.schedule_values.scheduling_values.length > index
                  ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                      .temp_sp_heating_mat_night_rel
                  : makePhase({ phase_id: index })
              }
              controls={controls}
              type="float"
            />
          )}
        </Box>
      </SettingGroupBlock>
    );
  } else {
    return <></>;
  }
}

function Co2ConcentrationSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.enable_co2_concentr) {
    return (
      <SettingGroupGrid controller={controller} id={'co2_concentration'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].co2_sp`}
          show={'co2_sp'}
          controller={controller}
          cur_value={phase.co2_sp}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index].co2_sp
              : makePhase({ phase_id: index })
          }
          controls={controls}
        />
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography>
            <Desc id="co2_control" />
          </Typography>
          <EnableDayTimeDisableSelect
            value={phase.co2_control}
            name="co2_control"
            onChange={(event) =>
              controls.setValue(
                `schedule_values.scheduling_values[${index}].co2_control`,
                event.target.value
              )
            }
          />
        </ValueBox>
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function Co2TemperSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.enable_co2_temper) {
    return (
      <SettingGroupGrid controller={controller} id={'temperature_co2'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].temp_sp_co2`}
          show={'temp_sp_co2'}
          controller={controller}
          cur_value={phase.temp_sp_co2}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index].temp_sp_co2
              : makePhase({ phase_id: index })
          }
          controls={controls}
          type="float"
        />
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function StartEndSettings({ index, values, phase, controls, controller }) {
  if (values.schedule_meta.enable_day_night) {
    return (
      <SettingGroup controller={controller} id={'start_end_time'}>
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography gutterBottom>
            <Desc id="start_day" show="start_day" />
          </Typography>
          <MyTimePicker
            value={phase.time_day_start}
            seconds={true}
            name="start_day"
            onChange={(value) =>
              controls.setValue(
                `schedule_values.scheduling_values[${index}].time_day_start`,
                value.toLocaleTimeString('de-DE')
              )
            }
          />
        </ValueBox>
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography gutterBottom>
            <Desc id="end_day" show="end_day" />
          </Typography>
          <MyTimePicker
            value={phase.time_day_end}
            seconds={true}
            name="end_day"
            onChange={(value) =>
              controls.setValue(
                `schedule_values.scheduling_values[${index}].time_day_end`,
                value.toLocaleTimeString('de-DE')
              )
            }
          />
        </ValueBox>
      </SettingGroup>
    );
  } else {
    return <></>;
  }
}

function DimChannelSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.enable_dim_ch_1) {
    return (
      <SettingGroupGrid controller={controller} id={'lights'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].lights_max_1`}
          show={'lights_max'}
          controller={controller}
          cur_value={phase.lights_max_1}
          controller_value={
            parent.settings.scheduling.schedule_values.scheduling_values.length > index
              ? parent.settings.scheduling.schedule_values.scheduling_values[index].lights_max_1
              : makePhase({ phase_id: index })
          }
          controls={controls}
        />
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function MultipleNutrientSettingsTabs({ index, values, phase, controls, controller, parent }) {
  const { t } = useTranslation();
  const nutrientIds = Object.keys(phase.nutrient_settings || {});
  const [activeNutrient, setActiveNutrient] = React.useState(nutrientIds[0] || '');
  React.useEffect(() => {
    if (!nutrientIds.includes(activeNutrient) && nutrientIds.length) {
      setActiveNutrient(nutrientIds[0]);
    }
  }, [nutrientIds, activeNutrient]);

  if (values.schedule_meta.enable_nutrient_1) {
    return (
      <TabContext value={activeNutrient}>
        <AppBar
          position="static"
          color="default"
          style={{ margin: '24px 24px 0', width: 'calc(100% - 48px)' }}
        >
          <Box display="flex" justifyContent="center" width="100%">
            <TabList
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, v) => setActiveNutrient(v)}
            >
              {nutrientIds.map((nid, idx) => (
                <Tab
                  key={nid}
                  value={nid}
                  label={`${t('nutrition')} ${idx + 1}`}
                  id={`phase-${index}-nutrient-tab-${idx}`}
                  aria-controls={`phase-${index}-nutrient-tabpanel-${idx}`}
                />
              ))}
            </TabList>
          </Box>
        </AppBar>

        {nutrientIds.map((nid, idx) => (
          <TabPanel
            key={nid}
            value={nid}
            id={`phase-${index}-nutrient-tabpanel-${idx}`}
            style={{ padding: 0 }}
          >
            <MultipleNutrientSettings
              id={Number(nid)}
              index={index}
              values={values}
              phase={phase}
              controls={controls}
              controller={controller}
              parent={parent}
            />
          </TabPanel>
        ))}
      </TabContext>
    );
  } else {
    return <></>;
  }
}

function MultipleNutrientSettings({ id, index, values, phase, controls, controller, parent }) {
  let { t } = useTranslation();

  if (values.schedule_meta.enable_nutrient_1) {
    return (
      <>
        <SettingGroupGrid controller={controller} id={'nutrient_solution'}>
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[${id}].ec_setpoint`}
            show={'ec_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[id].ec_setpoint}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .nutrient_settings[id].ec_setpoint
                : makePhase({ phase_id: index })
            }
            controls={controls}
            num_digits={2}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[${id}].ph_setpoint`}
            show={'ph_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[id].ph_setpoint}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .nutrient_settings[id].ph_setpoint
                : makePhase({ phase_id: index })
            }
            controls={controls}
            num_digits={2}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[${id}].temp_setpoint`}
            show={'temp_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[id].temp_setpoint}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .nutrient_settings[id].temp_setpoint
                : makePhase({ phase_id: index })
            }
            controls={controls}
            type="float"
          />
        </SettingGroupGrid>

        {parent.settings.nutrition.nutrient_settings[id].pumps.map((pump, i) => (
          <Pump
            id={id}
            index={i}
            name={pump.name}
            values={values}
            controls={controls}
            controller={controller}
            parent={parent}
            value_path_prefix={`schedule_values.scheduling_values[${index}].nutrient_settings[${id}].`}
            write_access={true /* write_access seems not implemented in scheduling yet */}
            edit_name={false}
          />
        ))}
      </>
    );
  } else {
    return <></>;
  }
}

function NutrientSettings({ index, values, phase, controls, controller, parent }) {
  let { t } = useTranslation();

  if (values.schedule_meta.enable_nutrient_1) {
    return (
      <>
        <SettingGroupGrid controller={controller} id={'nutrient_solution'}>
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[0].ec_setpoint`}
            show={'ec_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[0].ec_setpoint}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .nutrient_settings[0].ec_setpoint
                : makePhase({ phase_id: index })
            }
            controls={controls}
            num_digits={2}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[0].ph_setpoint`}
            show={'ph_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[0].ph_setpoint}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .nutrient_settings[0].ph_setpoint
                : makePhase({ phase_id: index })
            }
            controls={controls}
            num_digits={2}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[0].temp_setpoint`}
            show={'temp_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[0].temp_setpoint}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .nutrient_settings[0].temp_setpoint
                : makePhase({ phase_id: index })
            }
            controls={controls}
            type="float"
          />
        </SettingGroupGrid>

        {parent.settings.nutrition.pumps.map((pump, i) => (
          <Pump
            index={i}
            name={pump.name}
            values={values}
            controls={controls}
            controller={controller}
            parent={parent}
            value_path_prefix={`schedule_values.scheduling_values[${index}].nutrient_settings[0].`}
            write_access={true /* write_access seems not implemented in scheduling yet */}
            edit_name={false}
          />
        ))}
      </>
    );
  } else {
    return <></>;
  }
}

function MultipleIrrigationSettingsTabs({ index, values, phase, controls, controller, parent }) {
  const { t } = useTranslation();
  const irrigationIds = Object.keys(phase.irrigation_settings || {});
  const [activeIrrigation, setActiveIrrigation] = React.useState(irrigationIds[0] || '');
  React.useEffect(() => {
    if (!irrigationIds.includes(activeIrrigation) && irrigationIds.length) {
      setActiveIrrigation(irrigationIds[0]);
    }
  }, [irrigationIds, activeIrrigation]);

  if (values.schedule_meta.enable_irrigation_1) {
    return (
      <TabContext value={activeIrrigation}>
        <AppBar
          position="static"
          color="default"
          style={{ margin: '24px 24px 0', width: 'calc(100% - 48px)' }}
        >
          <Box display="flex" justifyContent="center" width="100%">
            <TabList
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, v) => setActiveIrrigation(v)}
            >
              {irrigationIds.map((iid, idx) => (
                <Tab
                  key={iid}
                  value={iid}
                  label={`${t('irrigation')} ${idx + 1}`}
                  id={`phase-${index}-irrigation-tab-${idx}`}
                  aria-controls={`phase-${index}-irrigation-tabpanel-${idx}`}
                />
              ))}
            </TabList>
          </Box>
        </AppBar>

        {irrigationIds.map((iid, idx) => (
          <TabPanel
            key={iid}
            value={iid}
            id={`phase-${index}-irrigation-tabpanel-${idx}`}
            style={{ padding: 0 }}
          >
            <MultipleIrrigationSettings
              id={Number(iid)}
              index={index}
              values={values}
              phase={phase}
              controls={controls}
              controller={controller}
              parent={parent}
            />
          </TabPanel>
        ))}
      </TabContext>
    );
  } else {
    return <></>;
  }
}

function MultipleIrrigationSettings({ id, index, values, phase, controls, controller, parent }) {
  let { t } = useTranslation();

  function makeTimeMin(prefix) {
    // Calculate total time in minutes
    const totalMinutes =
      Number(parent.settings.timing[`${prefix}_hour`]) * 60 +
      Number(parent.settings.timing[`${prefix}_min`]);

    return totalMinutes;
  }

  function parseTimeMin(settings, prefix) {
    if (typeof settings[`${prefix}`] === 'string') {
      const parts = settings[`${prefix}`].split(':').map(Number);

      // Calculate total time in minutes
      const totalMinutes = parts[0] * 60 + parts[1];

      return totalMinutes;
    }

    return settings[`${prefix}`];
  }

  function getDayRangeMin() {
    const start = makeTimeMin('start_day');
    let end = makeTimeMin('end_day');
    // If end is before or equal to start, it means it crosses midnight
    if (end <= start) {
      end += 24 * 60; // add 24 hours in minutes
    }
    return { min: start, max: end };
  }

  const { min, max } = getDayRangeMin();

  if (values.schedule_meta.enable_irrigation_1) {
    return (
      <>
        <ExpandableSettingsGroupGrid label={t('more_irrigation_set_hl')} expand_on_load={true}>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <MuiSwitch
                checked={phase.irrigation_settings[id].enable_irrigation === 1}
                onClick={(event) => {
                  controls.setValue(
                    `schedule_values.scheduling_values[${index}].irrigation_settings[${id}].enable_irrigation`,
                    event.target.checked ? 1 : 0
                  );
                }}
                color="primary"
              />
              <Desc id="enable_irrigation" />
            </Typography>
          </ValueBox>
        </ExpandableSettingsGroupGrid>

        <ExpandableSettingsGroupGrid label={t('phase_0_activation')}>
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[${id}].irrigation_p0_dry_back_abs`}
            show={'irrigation_p0_dry_back_abs'}
            controller={controller}
            cur_value={phase.irrigation_settings[id].irrigation_p0_dry_back_abs}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[id].irrigation_p0_dry_back_abs
                : makePhase({ phase_id: index })
            }
            controls={controls}
          />
        </ExpandableSettingsGroupGrid>

        <ExpandableSettingsGroupGrid label={t('phase_2_maintain')}>
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[${id}].irrigation_p2_offset_target_vwc`}
            show={'irrigation_p2_offset_target_vwc'}
            controller={controller}
            cur_value={phase.irrigation_settings[id].irrigation_p2_offset_target_vwc}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[id].irrigation_p2_offset_target_vwc
                : makePhase({ phase_id: index })
            }
            controls={controls}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[${id}].irrigation_p2_target_vwc_abs`}
            show={'irrigation_p2_target_vwc_abs'}
            controller={controller}
            cur_value={phase.irrigation_settings[id].irrigation_p2_target_vwc_abs}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[id].irrigation_p2_target_vwc_abs
                : makePhase({ phase_id: index })
            }
            controls={controls}
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[${id}].irrigation_p2_dry_back_abs`}
            show={'irrigation_p2_dry_back_abs'}
            controller={controller}
            cur_value={phase.irrigation_settings[id].irrigation_p2_dry_back_abs}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[id].irrigation_p2_dry_back_abs
                : makePhase({ phase_id: index })
            }
            controls={controls}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[${id}].irrigation_p2_end_before_sunset`}
            show={'irrigation_p2_end_before_sunset'}
            controller={controller}
            min={min}
            max={max}
            cur_value={parseTimeMin(
              phase.irrigation_settings[id],
              'irrigation_p2_end_before_sunset'
            )}
            controller_value={parseTimeMin(
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[id]
                : makePhase({ phase_id: index }),
              'irrigation_p2_end_before_sunset'
            )}
            controls={controls}
          />
        </ExpandableSettingsGroupGrid>
      </>
    );
  } else {
    return <></>;
  }
}

function IrrigationSettings({ index, values, phase, controls, controller, parent }) {
  let { t } = useTranslation();

  function makeTimeMin(prefix) {
    // Calculate total time in minutes
    const totalMinutes =
      Number(parent.settings.timing[`${prefix}_hour`]) * 60 +
      Number(parent.settings.timing[`${prefix}_min`]);

    return totalMinutes;
  }

  function parseTimeMin(settings, prefix) {
    if (typeof settings[`${prefix}`] === 'string') {
      const parts = settings[`${prefix}`].split(':').map(Number);

      // Calculate total time in minutes
      const totalMinutes = parts[0] * 60 + parts[1];

      return totalMinutes;
    }

    return settings[`${prefix}`];
  }

  function getDayRangeMin() {
    const start = makeTimeMin('start_day');
    let end = makeTimeMin('end_day');
    // If end is before or equal to start, it means it crosses midnight
    if (end <= start) {
      end += 24 * 60; // add 24 hours in minutes
    }
    return { min: start, max: end };
  }

  const { min, max } = getDayRangeMin();

  if (values.enable_irrigation_1) {
    return (
      <>
        <ExpandableSettingsGroupGrid label={t('more_irrigation_set_hl')} expand_on_load={true}>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <MuiSwitch
                checked={phase.irrigation_settings[0].enable_irrigation === 1}
                onClick={(event) => {
                  controls.setValue(
                    `schedule_values.scheduling_values[${index}].irrigation_settings[0].enable_irrigation`,
                    event.target.checked ? 1 : 0
                  );
                }}
                color="primary"
              />
              <Desc id="enable_irrigation" />
            </Typography>
          </ValueBox>
        </ExpandableSettingsGroupGrid>

        <ExpandableSettingsGroupGrid label={t('phase_0_activation')}>
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[0].irrigation_p0_dry_back_abs`}
            show={'irrigation_p0_dry_back_abs'}
            controller={controller}
            cur_value={phase.irrigation_settings[0].irrigation_p0_dry_back_abs}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[0].irrigation_p0_dry_back_abs
                : makePhase({ phase_id: index })
            }
            controls={controls}
          />
        </ExpandableSettingsGroupGrid>

        <ExpandableSettingsGroupGrid label={t('phase_2_maintain')}>
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[0].irrigation_p2_offset_target_vwc`}
            show={'irrigation_p2_offset_target_vwc'}
            controller={controller}
            cur_value={phase.irrigation_settings[0].irrigation_p2_offset_target_vwc}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[0].irrigation_p2_offset_target_vwc
                : makePhase({ phase_id: index })
            }
            controls={controls}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[0].irrigation_p2_target_vwc_abs`}
            show={'irrigation_p2_target_vwc_abs'}
            controller={controller}
            cur_value={phase.irrigation_settings[0].irrigation_p2_target_vwc_abs}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[0].irrigation_p2_target_vwc_abs
                : makePhase({ phase_id: index })
            }
            controls={controls}
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[0].irrigation_p2_dry_back_abs`}
            show={'irrigation_p2_dry_back_abs'}
            controller={controller}
            cur_value={phase.irrigation_settings[0].irrigation_p2_dry_back_abs}
            controller_value={
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[0].irrigation_p2_dry_back_abs
                : makePhase({ phase_id: index })
            }
            controls={controls}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].irrigation_settings[0].irrigation_p2_end_before_sunset`}
            show={'irrigation_p2_end_before_sunset'}
            controller={controller}
            min={min}
            max={max}
            cur_value={parseTimeMin(
              phase.irrigation_settings[0],
              'irrigation_p2_end_before_sunset'
            )}
            controller_value={parseTimeMin(
              parent.settings.scheduling.schedule_values.scheduling_values.length > index
                ? parent.settings.scheduling.schedule_values.scheduling_values[index]
                    .irrigation_settings[0]
                : makePhase({ phase_id: index }),
              'irrigation_p2_end_before_sunset'
            )}
            controls={controls}
          />
        </ExpandableSettingsGroupGrid>
      </>
    );
  } else {
    return <></>;
  }
}

function IntervalSettings({ index, id, timer, values, setValues, phase, controls, controller }) {
  if (values.schedule_meta[`enable_interval_${id}`]) {
    if (controller.type !== 6000) {
      return (
        <>
          <SettingGroup controller={controller} id={'interval_day'}>
            <ValueBox controller={controller} id={'scheduling_'}>
              <Typography gutterBottom>
                <Desc id="interval_on_day" show="interval_on_day" />
              </Typography>
              <MyTimePicker
                value={phase[`time_interval_${id}_on_day`]}
                seconds={true}
                name="interval_on_day"
                onChange={(value) =>
                  controls.setValue(
                    `schedule_values.scheduling_values[${index}].time_interval_${id}_on_day`,
                    value.toLocaleTimeString('de-DE')
                  )
                }
              />
            </ValueBox>
            <ValueBox controller={controller} id={'scheduling_'}>
              <Typography gutterBottom>
                <Desc id="interval_off_day" show="interval_off_day" />
              </Typography>
              <MyTimePicker
                value={phase[`time_interval_${id}_off_day`]}
                seconds={true}
                name="interval_off_day"
                onChange={(value) =>
                  controls.setValue(
                    `schedule_values.scheduling_values[${index}].time_interval_${id}_off_day`,
                    value.toLocaleTimeString('de-DE')
                  )
                }
              />
            </ValueBox>
          </SettingGroup>

          <SettingGroup controller={controller} id={'interval_night'}>
            <ValueBox controller={controller} id={'scheduling_'}>
              <Typography gutterBottom>
                <Desc id="interval_on_night" show="interval_on_night" />
              </Typography>
              <MyTimePicker
                value={phase[`time_interval_${id}_on_night`]}
                seconds={true}
                name="interval_on_night"
                onChange={(value) =>
                  controls.setValue(
                    `schedule_values.scheduling_values[${index}].time_interval_${id}_on_night`,
                    value.toLocaleTimeString('de-DE')
                  )
                }
              />
            </ValueBox>
            <ValueBox controller={controller} id={'scheduling_'}>
              <Typography gutterBottom>
                <Desc id="interval_off_night" show="interval_off_night" />
              </Typography>
              <MyTimePicker
                value={phase[`time_interval_${id}_off_night`]}
                seconds={true}
                name="interval_off_night"
                onChange={(value) =>
                  controls.setValue(
                    `schedule_values.scheduling_values[${index}].time_interval_${id}_off_night`,
                    value.toLocaleTimeString('de-DE')
                  )
                }
              />
            </ValueBox>
          </SettingGroup>
        </>
      );
    } else if (controller.type === 6000) {
      return (
        <IntervalTimerIndustrialPro
          index={index}
          id={timer.id}
          values={values}
          setValues={setValues}
          controls={controls}
          value_path_prefix={`schedule_values.scheduling_values[${index}].interval_timer[${index}]`}
        />
      );
    }
  } else {
    return <></>;
  }
}

function EventSettings({
  event_num,
  index,
  values,
  setValues,
  phase,
  controls,
  controller,
  timer
}) {
  const xId = (pre, post) => {
    return pre + event_num + (post !== undefined ? post : '');
  };

  if (values.schedule_meta[`enable_event_${event_num}`]) {
    if (controller?.type === undefined || [5155].includes(controller?.type)) {
      return (
        <SettingGroup controller={controller} id={xId('event_')}>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <Desc id={xId('time_event', '_start')} show="event_start" />
            </Typography>
            <MyTimePicker
              value={phase[`time_event${event_num}_start`]}
              seconds={true}
              name={xId('event', '_start')}
              onChange={(value) =>
                controls.setValue(
                  `schedule_values.scheduling_values[${index}].time_event${event_num}_start`,
                  value.toLocaleTimeString('de-DE')
                )
              }
            />
          </ValueBox>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <Desc id={xId('time_event', '_duration')} show="event_duration" />
            </Typography>
            <MyTimePicker
              value={phase[`time_event${event_num}_duration`]}
              seconds={true}
              name={xId('event', '_duration')}
              onChange={(value) =>
                controls.setValue(
                  `schedule_values.scheduling_values[${index}].time_event${event_num}_duration`,
                  value.toLocaleTimeString('de-DE')
                )
              }
            />
          </ValueBox>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <Desc id={xId('time_event', '_exec_gap')} show="event_exec_gap" />
            </Typography>
            <EventGapSelect
              value={phase[`time_event${event_num}_exec_gap`]}
              onChange={(event) =>
                controls.setValue(
                  `schedule_values.scheduling_values[${index}].time_event${event_num}_exec_gap`,
                  event.target.value
                )
              }
            />
          </ValueBox>
        </SettingGroup>
      );
    } else if (controller.type === 6000) {
      return (
        <EventTimerIndustrialPro
          id={timer.id}
          index={index}
          values={values}
          setValues={setValues}
          controls={controls}
          value_path_prefix={`schedule_values.scheduling_values[${index}].`}
          allowDelete={false}
        />
      );
    }
  }

  return <></>;
}
